import { useEffect } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { redirectToSignIn } from "../api/session";
import { LoadingState } from "../components/states";
import { HomeRoute } from "../routes/HomeRoute";
import styles from "./SignInRedirect.module.css";
import { useSession } from "./SessionContext";

/** The per-tab record of the last sign-in attempt, so a failed round trip cannot loop. */
const attemptStorageKey = "aia-portal:sign-in-attempt";

/**
 * How long a sign-in attempt for the same path counts as the one in progress. Sign-in can take a
 * while (a password, an MFA prompt), but a second attempt for the same path inside this window
 * means the first did not leave the browser with a session.
 */
const attemptWindowMilliseconds = 5 * 60 * 1000;

interface SignInAttempt {
  path: string;
  startedAt: number;
}

function readAttempt(): SignInAttempt | undefined {
  try {
    const raw = window.sessionStorage.getItem(attemptStorageKey);
    return raw === null ? undefined : (JSON.parse(raw) as SignInAttempt);
  } catch {
    // Storage can be blocked. Without it the redirect may repeat, which is the lesser failure.
    return undefined;
  }
}

function writeAttempt(attempt: SignInAttempt): void {
  try {
    window.sessionStorage.setItem(attemptStorageKey, JSON.stringify(attempt));
  } catch {
    // Storage can be blocked. The attempt still proceeds; only the loop guard is lost.
  }
}

function clearAttempt(): void {
  try {
    window.sessionStorage.removeItem(attemptStorageKey);
  } catch {
    // Nothing was recorded if storage is blocked, so there is nothing to clear.
  }
}

/**
 * Makes a deep link sign-in first. Until the session is known, the page is held back so that no
 * screen calls its API and shows a permission error. A signed-out visitor to any path other than
 * the root is sent to the identity provider at once, and returned to that path afterwards. The root
 * stays public: it is the overview a signed-out visitor is meant to see.
 *
 * A deep link is never shown to someone who is not signed in: when the browser is not being sent to
 * sign in (a sign-in attempt for this path is already under way, or the session could not be checked
 * at all), the root's page is shown in its place, at the same address, so a reload after signing in
 * lands where the person meant to go.
 *
 * @param props - The application routes to hold back until the session is known.
 */
export function SignInRedirect({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const location = useLocation();
  const returnPath = `${location.pathname}${location.search}`;
  const isDeepLink = location.pathname !== "/";

  const needsSignIn = isDeepLink && snapshot.status === "signed-out";
  const attempt = needsSignIn ? readAttempt() : undefined;
  const attemptInProgress =
    attempt?.path === returnPath &&
    Date.now() - attempt.startedAt < attemptWindowMilliseconds;
  const redirecting = needsSignIn && !attemptInProgress;

  useEffect(() => {
    if (snapshot.status === "signed-in" || snapshot.status === "unclassified") {
      clearAttempt();
    }
  }, [snapshot.status]);

  useEffect(() => {
    if (redirecting) {
      writeAttempt({ path: returnPath, startedAt: Date.now() });
      redirectToSignIn(returnPath);
    }
  }, [redirecting, returnPath]);

  if (redirecting) {
    return (
      <section className={styles.panel}>
        <LoadingState label={t("session.redirectingToSignIn")} />
      </section>
    );
  }

  if (isDeepLink && (snapshot.status === "signed-out" || snapshot.status === "error")) {
    return <HomeRoute />;
  }

  if (isDeepLink && snapshot.status === "loading") {
    return (
      <section className={styles.panel}>
        <LoadingState />
      </section>
    );
  }

  return <>{children}</>;
}
